// Copyright (c) 2026 Watch Tower Bible and Tract Society of Pennsylvania
// All Rights Reserved. Proprietary and confidential.
// Unauthorized copying, modification, or distribution is strictly prohibited.
import { useCallback } from 'react';
import { PDFDocument, PDFTextField, PDFCheckBox, PDFDropdown, PDFRadioGroup } from 'pdf-lib';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Hook que centraliza toda a lógica de geração/manipulação de PDFs de auditoria.
 * Usado por AuditFormPage (download) e AuditFormEditorPage (flatten, fill, save).
 */
export function usePdfGenerator() {

  /**
   * Preenche campos do PDF usando a API nativa do pdf-lib e faz flatten.
   * Busca o template limpo da API para garantir AcroForm intacto.
   * @param {string} formType - 's25b' ou 's45b'
   * @param {Object} fieldValues - { fieldName: value }
   * @returns {Promise<Uint8Array>} bytes do PDF flattenado
   */
  const flattenPdfWithValues = useCallback(async (formType, fieldValues) => {
    const { StandardFonts } = await import('pdf-lib');

    const templateRes = await axios.get(
      `${API}/audit-forms/template/${formType}/download`,
      { responseType: 'arraybuffer' }
    );
    const pdfDoc = await PDFDocument.load(templateRes.data, { ignoreEncryption: true });
    const pdfForm = pdfDoc.getForm();
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const pdfFields = pdfForm.getFields();

    pdfFields.forEach(field => {
      const name = field.getName();
      const val = fieldValues[name];
      try {
        if (field instanceof PDFTextField) {
          const strVal = (val !== undefined && val !== null && typeof val !== 'boolean') ? String(val) : '';
          const tf = pdfForm.getTextField(name);
          tf.setFontSize(0);
          tf.setText(strVal);
          try { tf.updateAppearances(font); } catch (e) { console.warn('Appearance update skipped:', name, e.message); }
        } else if (field instanceof PDFCheckBox) {
          const cb = pdfForm.getCheckBox(name);
          val ? cb.check() : cb.uncheck();
        } else if (field instanceof PDFDropdown && val) {
          pdfForm.getDropdown(name).select(String(val));
        } else if (field instanceof PDFRadioGroup && val) {
          pdfForm.getRadioGroup(name).select(String(val));
        }
      } catch (e) { console.warn('Field fill skipped:', name, e.message); }
    });

    try { pdfForm.updateFieldAppearances(font); } catch (e) { console.warn('Global appearance update skipped:', e.message); }
    pdfForm.flatten();
    return await pdfDoc.save();
  }, []);

  /**
   * Preenche campos do PDF sem flatten (para salvar estado editável).
   * @param {Uint8Array} pdfBytes - bytes do PDF original
   * @param {Array} fields - [{name, type, ...}]
   * @param {Object} fieldValues - { fieldName: value }
   * @returns {Promise<Uint8Array>} bytes do PDF com campos preenchidos
   */
  const fillPdfFields = useCallback(async (pdfBytes, fields, fieldValues) => {
    const { StandardFonts } = await import('pdf-lib');
    const pdfDoc = await PDFDocument.load(pdfBytes, { ignoreEncryption: true });
    const form = pdfDoc.getForm();
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

    fields.forEach(field => {
      try {
        const val = fieldValues[field.name];
        if (field.type === 'PDFTextField') {
          const tf = form.getTextField(field.name);
          tf.setFontSize(0);
          tf.setText(String(val || ''));
          try { tf.updateAppearances(font); } catch (e) { console.warn('Appearance update skipped:', field.name, e.message); }
        } else if (field.type === 'PDFCheckBox') {
          const cb = form.getCheckBox(field.name);
          val ? cb.check() : cb.uncheck();
        } else if (field.type === 'PDFDropdown' && val) {
          form.getDropdown(field.name).select(val);
        } else if (field.type === 'PDFRadioGroup' && val) {
          form.getRadioGroup(field.name).select(val);
        }
      } catch (e) { console.warn('Field fill skipped:', field.name, e.message); }
    });

    try { form.updateFieldAppearances(font); } catch (e) { console.warn('Global appearance update skipped:', e.message); }
    return await pdfDoc.save();
  }, []);

  /**
   * Dispara download de um PDF no navegador.
   * @param {Uint8Array|ArrayBuffer} bytes
   * @param {string} filename
   */
  const downloadPdf = useCallback((bytes, filename) => {
    const blob = new Blob([bytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  return { flattenPdfWithValues, fillPdfFields, downloadPdf };
}
